import { Badge, Tabs } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../context/AuthProvider';
import {
  updatePropsAll,
  updatePropsHistory,
  updatePropsRescheduled,
  updatePropsScheduled,
} from '../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { RootState } from '../../../store';
import MaintenanceHistoryTab from './MaintenanceHistoryTab/MaintenanceHistoryTab';
import MaintenancePlanningReschedulingRequestsTab from './MaintenancePlanningReschedulingRequestsTab/MaintenancePlanningReschedulingRequestsTab';
import MentanancePlanningAllTab from './MentanancePlanningAllTab/MentanancePlanningAllTab';
import MentenancePlanningReportTab from './MentenancePlanningReportTab/MentenancePlanningReportTab';
import MentenancePlanningScheduledTab from './MentenancePlanningScheduledTab/MentenancePlanningScheduledTab';
import MentenancePlanningSelectionTab from './MentenancePlanningSelectionTab/MentenancePlanningSelectionTab';

const MentenancePlanning: React.FC = () => {
  const dispatch = useDispatch();
  const { profile } = useAuth();
  const reduxMentenancePlanning = useSelector((state: RootState) => state.models.mentenancePlanning);
  const [activeKey, setActiveKey] = useState<string>('Toate');

  const userRoles = profile?.userPermission?.rols ?? [];
  const editableRoles = ['dispatcher', 'admin'];
  const isEditable = userRoles.some((role) => editableRoles.includes(role));

  // Mapping tabs to update configurations
  const updateConfig: Record<string, (() => void) | undefined> = {
    Toate: () => dispatch(updatePropsAll({ field: 'refresh', value: true })),
    Programate: () => dispatch(updatePropsScheduled({ field: 'refresh', value: true })),
    'Cerere Reprogramare': () => dispatch(updatePropsRescheduled({ field: 'refresh', value: true })),
    Istoric: () => dispatch(updatePropsHistory({ field: 'refresh', value: true })),
  };

  // Memoized function to reset search parameters
  const resetSearchParams = useCallback(
    (action?: () => void) => {
      [updatePropsAll, updatePropsScheduled, updatePropsRescheduled, updatePropsHistory].forEach((updateAction) => {
        dispatch(updateAction({ field: 'searchTerm', value: '' }));
        dispatch(updateAction({ field: 'skip', value: 0 }));
        dispatch(updateAction({ field: 'take', value: 10 }));
      });

      if (action) action(); // Only trigger the refresh for the active tab
    },
    [dispatch]
  );

  // Update search params based on active tab
  useEffect(() => {
    if (activeKey !== 'Selectate') {
      resetSearchParams(updateConfig[activeKey]);
    }
  }, [activeKey, resetSearchParams]);

  // Memoized tab items
  const tabItems = useMemo(
    () =>
      [
        { key: 'Toate', component: <MentanancePlanningAllTab isEditable={isEditable} /> },
        {
          key: 'Selectate',
          component: <MentenancePlanningSelectionTab />,
          badge: reduxMentenancePlanning.selectedRecords?.length,
        },
        { key: 'Programate', component: <MentenancePlanningScheduledTab /> },
        { key: 'Cerere Reprogramare', component: <MaintenancePlanningReschedulingRequestsTab /> },
        { key: 'Istoric', component: <MaintenanceHistoryTab /> },
        {
          key: 'Raport',
          component: profile?.userPermission?.rols.some((role) => ['dispatcher', 'admin'].includes(role)) ? (
            <MentenancePlanningReportTab />
          ) : null,
        },
      ].map(({ key, component, badge }) => ({
        key,
        label: badge ? (
          <>
            {key} <Badge count={badge} color='blue' />
          </>
        ) : (
          key
        ),
        children: component,
      })),
    [reduxMentenancePlanning.selectedRecords, profile?.userPermission?.rols]
  );

  return <Tabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />;
};

export default MentenancePlanning;
